﻿import { useEffect, useRef, useState } from 'react';

import Map from 'ol/Map';
import Control from 'ol/control/Control';
import { MapBrowserEvent } from 'ol';
import { toLonLat } from 'ol/proj';

import GeoJsonSpaceProperties from '../../../models/GeoJsonSpaceProperties';
import { PICK_PORT_CURSOR } from '../../../models/pickReports/PickReportsSettings';
import { IPickReportQueryExtended } from '../../../models/pickReports/IFetchPickReports';
import { getDepthFromEvent } from '../../../layers/ChartSubLayers/DepthLayerBase';
import { getFeaturesFromEvent } from '../../../layers/SpacesLayer';

/**
 * Contains all Openlayers logic for a pick report control
 */
export const useOpenLayerPickReportControl = (
    map: Map | undefined,
    active: boolean | undefined,
    busy: boolean | undefined,
    includeDepth: boolean | undefined,
    includeSpacesFeatures: boolean | undefined,
) => {

    const element = useRef<HTMLDivElement>(null);
    const container = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<{ busy?: boolean, includeDepth?: boolean, includeSpacesFeatures?: boolean }>({});

    const [mapReady, setMapReady] = useState<boolean>();
    const [query, setQuery] = useState<IPickReportQueryExtended>();

    useEffect(() => {
        optionsRef.current.busy = busy;
    }, [busy]);

    useEffect(() => {
        optionsRef.current.includeDepth = includeDepth;
    }, [includeDepth]);

    useEffect(() => {
        optionsRef.current.includeSpacesFeatures = includeSpacesFeatures;
    }, [includeSpacesFeatures]);

    useEffect(() => {
        if (map && element.current && container.current) {

            const control = new Control({
                target: container.current,
                element: element.current
            })

            map.addControl(control);

            const checkSize = () => {
                const size = map.getSize();
                const ready = !!size && size?.every(v => !!v);
                setMapReady(ready);

                if (!ready)
                    map.once('rendercomplete', checkSize);
            }
            checkSize();

            return () => {
                try {
                    if (map && control)
                        map.removeControl(control);
                }
                catch { }
            };
        }
    }, [map]);

    useEffect(() => {
        if (map && mapReady) {  
            if (active) {
                const click = (evt: MapBrowserEvent<UIEvent>) => {
                    //Note: this function cannot access states directly, therefore a ref is used
                    const { busy, includeDepth, includeSpacesFeatures } = optionsRef.current;

                    if (busy)
                        return;

                    const [lon, lat] = toLonLat(evt.coordinate, map.getView()?.getProjection());
                    const zoom = Math.round(evt.map.getView().getZoom() ?? 1);
                    const depth = includeDepth ? getDepthFromEvent(evt) : undefined;              

                    setQuery({
                        lon, lat, zoom, depth,
                        spaceFeatures: includeSpacesFeatures ? getFeaturesFromEvent(evt).map(f => {
                            const properties = f.properties as Partial<GeoJsonSpaceProperties>;
                            return {
                                id: properties.fid ?? properties.top_space_id,
                                properties,
                                geometry: f.geometry
                            }
                        }) : undefined
                    });
                }

                const move = () => {
                    const target = map?.getTargetElement();
                    if (target && !target.style.cursor) 
                        target.style.cursor = PICK_PORT_CURSOR;  
                }

                map.on("singleclick", click);
                map.on("pointermove", move);

                return () => {
                    map.un("singleclick", click);
                    map.un("pointermove", move);
                }
            }
            else {
                setQuery(undefined);
                const target = map?.getTargetElement();
                if (target && target.style.cursor === PICK_PORT_CURSOR)
                    target.style.cursor = '';                
            }
        }
    }, [map, mapReady, active]);

    return { query, container, element };
}